import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Check, Loader2, X } from 'lucide-react';
import {
  TIPOS, DISTRIBUCION, RAMPAS, ESCALERAS, GEOMETRIA, escalaPlano, xColumna,
} from './estacionamientoLogic';
import { Rampa, Escalera, GlifoTipo } from './Emblemas';
import Puesto from './Puesto';

const SEL_ANCHO = 264;
const SEL_ALTO = 208;
const ORDEN_TIPOS = ['libre', 'privado_sura', 'gerencia', 'privado'];

// Posicion del selector de tipo: debajo de los puestos de arriba y encima de los de abajo,
// siempre dentro del lienzo para que nunca se corte.
function posicionSelector(d) {
  const { ancho, alto, ySup, yInf, anchoPlano } = GEOMETRIA;
  const centro = xColumna(d.columna) + ancho / 2;
  const left = Math.max(8, Math.min(centro - SEL_ANCHO / 2, anchoPlano - SEL_ANCHO - 8));
  const top = d.fila === 'inferior'
    ? Math.max(8, yInf - SEL_ALTO - 10)
    : ySup + alto + 10;
  return { left, top };
}

function SelectorTipo({ puesto, guardando, onElegir, onCerrar, posicion }) {
  const ref = useRef(null);

  useEffect(() => {
    const actual = ref.current && ref.current.querySelector('button[aria-pressed="true"]');
    const primero = ref.current && ref.current.querySelector('button.est-sel-op');
    const objetivo = actual || primero;
    if (objetivo) objetivo.focus();
  }, []);

  return (
    <div
      ref={ref}
      className="est-selector"
      role="dialog"
      aria-label={`Cambiar el tipo del puesto ${puesto.numero}`}
      style={{ left: posicion.left, top: posicion.top }}
    >
      <div className="est-sel-cab">
        <span>Puesto {puesto.numero}</span>
        <button type="button" className="est-x" aria-label="Cerrar selector" onClick={onCerrar}>
          <X size={16} aria-hidden="true" />
        </button>
      </div>
      {ORDEN_TIPOS.map((t) => (
        <button
          key={t}
          type="button"
          className="est-sel-op"
          aria-pressed={puesto.tipo === t}
          disabled={guardando}
          onClick={() => onElegir(puesto.numero, t)}
        >
          <span className={`est-mini est-tipo-${t}`}>
            <GlifoTipo tipo={t} escala={18} />
          </span>
          <span className="est-sel-txt">{TIPOS[t].etiqueta}</span>
          {guardando && puesto.tipo === t ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : null}
          {!guardando && puesto.tipo === t ? <Check size={16} aria-hidden="true" /> : null}
        </button>
      ))}
    </div>
  );
}

export default function Plano({
  puestos, esGerencia, filtro, seleccionado, activo, modoEdicion, selectorPara, guardando,
  onEnter, onLeave, onFoco, onDesenfoque, onPulsar, onElegirTipo, onCerrarSelector,
}) {
  const sinMovimiento = Boolean(useReducedMotion());
  const contenedor = useRef(null);
  const [escala, setEscala] = useState(1);

  // El plano se escala para llenar el ancho de su tarjeta (con un minimo legible).
  useLayoutEffect(() => {
    const el = contenedor.current;
    if (!el) return undefined;
    const medir = () => setEscala(escalaPlano(el.clientWidth));
    medir();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', medir);
      return () => window.removeEventListener('resize', medir);
    }
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  // Cerrar el selector con Escape o al pulsar fuera de el y de los puestos.
  useEffect(() => {
    if (!selectorPara) return undefined;
    const cerrar = (devolverFoco) => {
      onCerrarSelector();
      if (devolverFoco && contenedor.current) {
        const boton = contenedor.current.querySelector(`[data-puesto="${selectorPara}"]`);
        if (boton) boton.focus();
      }
    };
    const alTeclear = (e) => { if (e.key === 'Escape') cerrar(true); };
    const alPulsar = (e) => {
      if (e.target.closest && (e.target.closest('.est-selector') || e.target.closest('.est-puesto'))) return;
      cerrar(false);
    };
    document.addEventListener('keydown', alTeclear);
    document.addEventListener('mousedown', alPulsar);
    document.addEventListener('touchstart', alPulsar);
    return () => {
      document.removeEventListener('keydown', alTeclear);
      document.removeEventListener('mousedown', alPulsar);
      document.removeEventListener('touchstart', alPulsar);
    };
  }, [selectorPara, onCerrarSelector]);

  const porNumero = {};
  (puestos || []).forEach((p) => { porNumero[p.numero] = p; });

  const { ancho, alto, ySup, yInf, anchoPlano, altoPlano, anchoRampa, altoRampa, anchoEscalera } = GEOMETRIA;
  const yMedio = Math.round((ySup + alto + yInf) / 2);

  // Rotulo "Disponibles para todo el personal de la torre" bajo el 135 y el 134.
  const d135 = DISTRIBUCION.find((d) => d.numero === 135);
  const d134 = DISTRIBUCION.find((d) => d.numero === 134);
  const anchoNota = 196;
  const centroNota = (xColumna(d135.columna) + xColumna(d134.columna) + ancho) / 2;

  const dSelector = selectorPara ? DISTRIBUCION.find((d) => d.numero === selectorPara) : null;
  const pSelector = dSelector ? porNumero[selectorPara] : null;

  return (
    <div className={`est-piso${modoEdicion ? ' est-edicion' : ''}`}>
      <div className="est-scroll" ref={contenedor}>
        <div className="est-escala" style={{ width: anchoPlano * escala, height: altoPlano * escala }}>
          <div
            className="est-lienzo"
            style={{ width: anchoPlano, height: altoPlano, transform: `scale(${escala})` }}
          >
            <div className="est-carril" style={{ top: yMedio }} aria-hidden="true" />

            {RAMPAS.map((r, i) => (
              <motion.div
                key={`rampa-${r.columna}`}
                className={`est-rampa${i === 1 ? ' est-rampa-b' : ''}`}
                style={{ left: xColumna(r.columna), top: ySup - (altoRampa - alto) / 2, width: anchoRampa, height: altoRampa }}
                initial={sinMovimiento ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.05 + i * 0.1 }}
                aria-hidden="true"
              >
                <Rampa ancho={anchoRampa} alto={altoRampa} />
              </motion.div>
            ))}

            <motion.div
              className="est-nota"
              style={{ left: centroNota - anchoNota / 2, top: ySup + alto + 16, width: anchoNota, height: 46 }}
              initial={sinMovimiento ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              Disponibles para todo el personal de la torre
            </motion.div>

            <motion.div
              className="est-escalera"
              style={{ left: xColumna(ESCALERAS.columna), top: ySup, width: anchoEscalera, height: alto }}
              initial={sinMovimiento ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.3 }}
            >
              <Escalera ancho={anchoEscalera} alto={alto} />
            </motion.div>

            {DISTRIBUCION.map((d) => {
              const p = porNumero[d.numero];
              if (!p) return null;
              const bloqueado = p.tipo === 'gerencia' && !esGerencia;
              const coincide = Boolean(filtro) && p.tipo === filtro;
              return (
                <Puesto
                  key={d.numero}
                  numero={d.numero}
                  tipo={p.tipo}
                  fila={d.fila}
                  retraso={0.05 + d.columna * 0.03 + (d.fila === 'inferior' ? 0.18 : 0)}
                  left={xColumna(d.columna)}
                  top={d.fila === 'superior' ? ySup : yInf}
                  ancho={ancho}
                  alto={alto}
                  sinMovimiento={sinMovimiento}
                  bloqueado={bloqueado}
                  atenuado={Boolean(filtro) && !coincide}
                  resaltado={coincide}
                  activo={activo === d.numero}
                  seleccionado={seleccionado === d.numero}
                  edicion={modoEdicion}
                  onEnter={onEnter}
                  onLeave={onLeave}
                  onFoco={onFoco}
                  onDesenfoque={onDesenfoque}
                  onPulsar={onPulsar}
                />
              );
            })}

            {dSelector && pSelector ? (
              <SelectorTipo
                key={selectorPara}
                puesto={pSelector}
                guardando={guardando === selectorPara}
                onElegir={onElegirTipo}
                onCerrar={onCerrarSelector}
                posicion={posicionSelector(dSelector)}
              />
            ) : null}
          </div>
        </div>
      </div>
      <p className="est-hint">Desliza el plano hacia los lados para ver todos los puestos</p>
    </div>
  );
}
